// frontend/src/components/PhoneVerification.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    CheckIcon,
    XIcon,
    PhoneIcon,
    UserIcon,
    CalendarIcon,
    IdentificationIcon,
    RefreshIcon
} from '@heroicons/react/outline';
import api from '../services/api';
import { showSuccess, showInfo } from '../utils/toastUtils';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../i18n/translations';

const PhoneVerification = () => {
    const { phoneToken } = useParams();
    const navigate = useNavigate();
    const [language, setLanguageLocal] = useState('en');
    const { setLanguage } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorCode, setErrorCode] = useState(null);
    const [sessionData, setSessionData] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [isStandardLink, setIsStandardLink] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // ⭐ NEW: completed-session state
    const [alreadyCompleted, setAlreadyCompleted] = useState(null);

    // ⭐ NEW: resuming state (small overlay)
    const [resuming, setResuming] = useState(false);

    // Form fields
    const [phoneNumber, setPhoneNumber] = useState('');
    const [aadharNumber, setAadharNumber] = useState('');
    const [fullName, setFullName] = useState('');
    const [age, setAge] = useState('');
    const [sex, setSex] = useState('');

    const t = (key, replacements) => getTranslation(language, key, replacements);

    useEffect(() => {
        if (phoneToken) {
            fetchSessionByPhoneToken();
        }
    }, [phoneToken]);

    const fetchSessionByPhoneToken = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/sessions/phone-token/${phoneToken}`);

            if (response.data.success) {
                const session = response.data.session;
                setSessionData(session);
                const lang = session.language || session.default_language || 'en';
                setLanguageLocal(lang);
                setLanguage(lang);
                setIsStandardLink(session.is_standard_link || false);
                setLoading(false);
            } else {
                setErrorCode('INVALID_TOKEN');
                setError(t('error.INVALID_TOKEN'));
                setLoading(false);
            }
        } catch (error) {
            console.error('Error fetching session:', error);
            setErrorCode('INVALID_TOKEN');
            setError(t('error.INVALID_TOKEN'));
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Prevent double submission
        if (submitted || submitting) {
            console.log('⚠️ Submission already in progress');
            return;
        }

        // Validate Phone Number - MANDATORY (10 digits)
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(phoneNumber)) {
            setErrorCode('PHONE_INVALID');
            setError(t('error.PHONE_INVALID'));
            return;
        }

        // Validate Aadhar Number - MANDATORY (12 digits)
        const aadharRegex = /^[0-9]{12}$/;
        if (!aadharRegex.test(aadharNumber)) {
            setErrorCode('AADHAR_INVALID');
            setError(t('error.AADHAR_INVALID'));
            return;
        }

        setSubmitting(true);
        setSubmitted(true);
        setError(null);
        setErrorCode(null);

        try {
            const response = await api.post(`/sessions/verify-phone/${phoneToken}`, {
                phone_number: phoneNumber,
                aadhar_number: aadharNumber,
                full_name: fullName || undefined,
                age: age || undefined,
                sex: sex || undefined,
                skip_demographics: isStandardLink
            });

            const data = response.data;

            if (!data.success) {
                setSubmitted(false);
                handleApiError(data);
                setSubmitting(false);
                return;
            }

            // ═══════════════════════════════════════════════════════
            // ⭐ CASE 1: Candidate already completed this assessment
            // ═══════════════════════════════════════════════════════
            if (data.already_completed) {
                console.log('✅ Already completed — showing final card');
                setAlreadyCompleted({
                    candidateName: data.candidate_name,
                    completedAt: data.completed_at,
                    totalScore: data.total_score,
                    grade: data.grade,
                    recommendation: data.recommendation,
                    meaning: data.meaning,
                    accessToken: data.access_token
                });
                setSubmitting(false);
                return;
            }

            // ═══════════════════════════════════════════════════════
            // ⭐ CASE 2: Candidate has an active session — resume it
            // ═══════════════════════════════════════════════════════
            if (data.resumed) {
                console.log('▶️ Resuming existing session');
                showInfo(t('verify.resuming_message') || 'Resuming your session...');
                setResuming(true);

                // Give the toast a moment to render, then redirect
                setTimeout(() => {
                    window.location.href = `/test/${data.access_token}`;
                }, 800);
                return;
            }

            // ═══════════════════════════════════════════════════════
            // CASE 3: Normal new session
            // ═══════════════════════════════════════════════════════
            showSuccess('✅ ' + t('verify.success'));
            window.location.href = `/test/${data.access_token}`;

        } catch (error) {
            console.error('Error verifying phone:', error);
            setSubmitted(false);
            handleApiError(error.response?.data || {});
            setSubmitting(false);
        }
    };

    // ============================================
    // MAP BACKEND ERROR → TRANSLATED MESSAGE
    // ============================================
    const handleApiError = (data) => {
        const code = data.error_code || 'SERVER_ERROR';
        setErrorCode(code);

        const translated = t(`error.${code}`);
        if (translated === `error.${code}` && data.error) {
            setError(data.error);
        } else {
            setError(translated);
        }
    };

    const getErrorHeader = () => {
        if (errorCode === 'AADHAR_IN_USE' || errorCode === 'PHONE_IN_USE') {
            return t('verify.duplicate_title');
        }
        return t('verify.failed_title');
    };

    const formatAadhar = (value) => {
        const cleaned = value.replace(/\D/g, '');
        return cleaned.slice(0, 12);
    };

    const formatPhone = (value) => {
        return value.replace(/\D/g, '');
    };

    // ═══════════════════════════════════════════════════════════
    // ⭐ RESUME LOADING SCREEN
    // ═══════════════════════════════════════════════════════════
    if (resuming) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border border-gray-200">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <RefreshIcon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h2 className="text-xl font-bold text-[#063b56] mb-2">
                        {t('verify.resuming_title') || 'Resuming Your Session'}
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                        {t('verify.resuming_message') || 'Welcome back! Continuing from where you left off...'}
                    </p>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff9f2f] mx-auto"></div>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════
    // ⭐ ALREADY COMPLETED SCREEN
    // ═══════════════════════════════════════════════════════════
    if (alreadyCompleted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb] py-8">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border border-gray-200">
                    {/* Success icon */}
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckIcon className="h-8 w-8 text-green-600" />
                    </div>

                    <h2 className="text-xl font-bold text-[#063b56] mb-2">
                        {t('verify.already_completed_title') || 'Assessment Already Completed'}
                    </h2>

                    <p className="text-sm text-gray-600 mb-6">
                        {t('verify.already_completed_message') ||
                            'You have already completed this assessment. Your responses have been recorded.'}
                    </p>

                    {/* Summary card */}
                    <div className="bg-gray-50 rounded-lg p-4 text-left mb-6 space-y-1.5">
                        {alreadyCompleted.candidateName && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Name</span>
                                <span className="text-gray-800 font-medium text-right ml-2 truncate">
                                    {alreadyCompleted.candidateName}
                                </span>
                            </div>
                        )}
                        {alreadyCompleted.completedAt && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Completed</span>
                                <span className="text-gray-800 font-medium">
                                    {new Date(alreadyCompleted.completedAt).toLocaleString()}
                                </span>
                            </div>
                        )}
                        {alreadyCompleted.grade && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Grade</span>
                                <span className="text-gray-800 font-medium">
                                    {alreadyCompleted.grade}
                                </span>
                            </div>
                        )}
                        {alreadyCompleted.recommendation && (
                            <div className="pt-2 mt-2 border-t border-gray-200">
                                <p className="text-[11px] text-gray-500 mb-1">Outcome</p>
                                <p className="text-xs font-medium text-gray-800 leading-snug">
                                    {alreadyCompleted.recommendation}
                                </p>
                            </div>
                        )}
                    </div>

                    <p className="text-xs text-gray-400 mb-4">
                        If you believe this is an error, please contact the assessment team.
                    </p>

                    {/* View report button (optional) */}
                    {alreadyCompleted.accessToken && (
                        <button
                            onClick={() => window.location.href = `/results/${alreadyCompleted.accessToken}`}
                            className="w-full px-6 py-2.5 bg-[#063b56] text-white rounded-lg hover:bg-[#0a4a6a] font-medium"
                        >
                            {t('verify.view_result') || 'View Summary'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ff9f2f] mx-auto"></div>
                    <p className="mt-4 text-gray-600">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (error && !sessionData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb]">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center border border-gray-200">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <XIcon className="h-8 w-8 text-red-500" />
                    </div>

                    <h2 className="text-xl font-bold text-gray-800 mb-3">
                        {getErrorHeader()}
                    </h2>

                    <p className="text-gray-600 italic">
                        {error}
                    </p>

                    <button
                        onClick={() => window.location.reload()}
                        className="mt-6 px-6 py-2.5 bg-[#ff9f2f] text-white rounded-lg hover:bg-[#e68a1a] font-medium"
                    >
                        {t('verify.try_again')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f4f6fb] py-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full border border-gray-200">

                {/* Header */}
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#063b56] rounded-full flex items-center justify-center mx-auto mb-4">
                        <IdentificationIcon className="h-8 w-8 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-[#063b56]">
                        {t('verify.title')}
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        {t('verify.subtitle')}
                    </p>

                    {isStandardLink && (
                        <div className="mt-2 p-2 bg-green-50 rounded-lg border border-green-200">
                            <p className="text-xs text-green-600 font-medium">
                                ✅ {t('verify.standard_link_notice')}
                            </p>
                            <p className="text-xs text-green-500 mt-1">
                                {t('verify.standard_link_notice2')}
                            </p>
                        </div>
                    )}
                </div>

                {/* Session Info */}
                {sessionData && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <p className="text-sm text-gray-600">
                            <strong>Assessment:</strong> {sessionData.assessment_name || 'N/A'}
                        </p>
                        {sessionData.department_name && (
                            <p className="text-sm text-gray-600">
                                <strong>Department:</strong> {sessionData.department_name}
                            </p>
                        )}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    {/* Phone Number */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('verify.phone_label')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <PhoneIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(formatPhone(e.target.value))}
                                placeholder={t('verify.phone_placeholder')}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9f2f]"
                                maxLength={10}
                                autoFocus
                                disabled={submitting}
                                required
                            />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{t('verify.phone_hint')}</p>
                    </div>

                    {/* Aadhar Number */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('verify.aadhar_label')} <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <IdentificationIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={aadharNumber}
                                onChange={(e) => setAadharNumber(formatAadhar(e.target.value))}
                                placeholder={t('verify.aadhar_placeholder')}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9f2f]"
                                maxLength={12}
                                disabled={submitting}
                                required
                            />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{t('verify.aadhar_hint')}</p>
                    </div>

                    {/* Full Name */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('verify.name_label')}{' '}
                            <span className="text-gray-400 text-xs">({t('common.optional')})</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <UserIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                placeholder={t('verify.name_placeholder')}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9f2f]"
                                disabled={submitting}
                            />
                        </div>
                    </div>

                    {/* Age */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('verify.age_label')}{' '}
                            <span className="text-gray-400 text-xs">({t('common.optional')})</span>
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <CalendarIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="number"
                                value={age}
                                onChange={(e) => setAge(e.target.value)}
                                placeholder={t('verify.age_placeholder')}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9f2f]"
                                min="1"
                                max="120"
                                disabled={submitting}
                            />
                        </div>
                    </div>

                    {/* Sex */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('verify.sex_label')}{' '}
                            <span className="text-gray-400 text-xs">({t('common.optional')})</span>
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            {['Male', 'Female', 'Other'].map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => setSex(option)}
                                    className={`py-3 border rounded-lg transition ${sex === option
                                        ? 'border-[#ff9f2f] bg-[#ff9f2f]/10 text-[#063b56]'
                                        : 'border-gray-300 hover:border-gray-400 text-gray-600'
                                        }`}
                                    disabled={submitting}
                                >
                                    {t(`verify.sex_${option.toLowerCase()}`)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Inline error */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={submitting || phoneNumber.length < 10 || aadharNumber.length < 12}
                        className="w-full bg-[#ff9f2f] text-white py-3 rounded-lg hover:bg-[#e68a1a] disabled:opacity-50 transition-all flex items-center justify-center shadow-sm font-medium"
                    >
                        {submitting ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                {submitted ? t('verify.redirecting') : t('verify.submitting')}
                            </>
                        ) : (
                            <>
                                <CheckIcon className="h-5 w-5 mr-2" />
                                {t('verify.submit')}
                            </>
                        )}
                    </button>
                </form>

                {/* Footer */}
                <p className="text-xs text-gray-400 text-center mt-4">
                    {isStandardLink ? (
                        <>
                            {t('verify.standard_link_notice')}
                            <br />
                            {t('verify.standard_link_notice2')}
                        </>
                    ) : (
                        t('verify.privacy_notice')
                    )}
                    <br />
                    {t('verify.one_time_notice')}
                </p>
            </div>
        </div>
    );
};

export default PhoneVerification;